import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Shield, CheckCircle, Clock, AlertCircle, 
  User, FileText, RefreshCw, Check, X
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

export default function LandlordVerification() {
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    loadUserAndVerification();
  }, []);

  const loadUserAndVerification = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${getToken()}` };
      
      // Get current user
      const userRes = await fetch(`${API_URL}/api/auth/user/me`, { headers });
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserId(userData.id);
        
        // Get verification status
        const verifyRes = await fetch(`${API_URL}/api/verification/user/${userData.id}/status`, { headers });
        if (verifyRes.ok) {
          setVerificationStatus(await verifyRes.json());
        }
      }
    } catch (error) {
      console.error('Failed to load verification:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestVerification = async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/verification/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject_id: userId,
          subject_type: 'landlord',
          trigger_context: 'onboarding'
        })
      });
      
      if (response.ok) {
        toast.success('Verification requested successfully');
        loadUserAndVerification();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to request verification');
      }
    } catch (error) {
      toast.error('Failed to request verification');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': case 'passed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress': case 'pending': case 'queued': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'failed': return <X className="w-5 h-5 text-red-500" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': case 'passed': return 'bg-green-100 text-green-700';
      case 'in_progress': case 'pending': case 'queued': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-7 h-7 text-blue-600" />
              Identity Verification
            </h1>
            <p className="text-gray-600 mt-1">Complete your KYC/AML verification</p>
          </div>
          <Button onClick={loadUserAndVerification} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Overall Status */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${
                verificationStatus?.verification_status === 'verified' 
                  ? 'bg-green-100' : 'bg-yellow-100'
              }`}>
                {verificationStatus?.verification_status === 'verified' 
                  ? <CheckCircle className="w-8 h-8 text-green-600" />
                  : <Clock className="w-8 h-8 text-yellow-600" />
                }
              </div>
              <div>
                <h2 className="text-lg font-semibold">Verification Status</h2>
                <p className="text-gray-600 capitalize">
                  {verificationStatus?.verification_status || 'Not Started'}
                </p>
              </div>
            </div>
            
            {verificationStatus?.verification_status !== 'verified' && 
             verificationStatus?.verification_status !== 'in_progress' && (
              <Button onClick={requestVerification}>
                Start Verification
              </Button>
            )}
          </div>
        </Card>

        {/* Verification Checks */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">Verification Checks</h3>
          </div>
          
          <div className="divide-y">
            {/* ID Verification */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">Identity Document</div>
                  <div className="text-sm text-gray-500">Passport or driving licence</div>
                </div>
              </div>
              {getStatusIcon(verificationStatus?.kyc_status)}
            </div>
            
            {/* Address Verification */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">Address Verification</div>
                  <div className="text-sm text-gray-500">Proof of address (utility bill, bank statement)</div>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${getStatusColor(verificationStatus?.case?.status || 'not_started')}`}>
                {verificationStatus?.case?.status || 'Not Started'}
              </span>
            </div>
            
            {/* AML Check */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="font-medium">AML/PEP Screening</div>
                  <div className="text-sm text-gray-500">Anti-money laundering check</div>
                </div>
              </div>
              {getStatusIcon(verificationStatus?.aml_status)}
            </div>
          </div>
        </Card>

        {/* Individual Checks from Case */}
        {verificationStatus?.checks?.length > 0 && (
          <Card className="mt-6 overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold">Detailed Check Results</h3>
            </div>
            <div className="divide-y">
              {verificationStatus.checks.map((check) => (
                <div key={check.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium capitalize">{check.check_type.replace('_', ' ')}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(check.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(check.status)}`}>
                    {check.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Info */}
        <Card className="mt-6 p-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">Why Verification Matters</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Protects you and your tenants from fraud</li>
            <li>• Required for deposit protection schemes</li>
            <li>• Ensures compliance with UK rental regulations</li>
            <li>• Verification is processed within 24-48 hours</li>
          </ul>
        </Card>
      </div>
    </DashboardLayout>
  );
}
